import { useEffect, useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { createFormField, FormFieldBlueprint } from '@backstage/plugin-scaffolder-react/alpha';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type Org = { slug: string; name: string; acronym: string };

const Component = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, rawErrors, required, formData, schema } = props;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [items, setItems] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('xvia-cms-api');
        const r = await fetchApi.fetch(`${baseUrl}/organizations`);
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
        const data = (await r.json()) as { organizations: Org[] };
        if (!cancelled) setItems(data.organizations ?? []);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'erro ao listar órgãos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  const label = schema.title ?? 'Órgão';
  const hasError = !!rawErrors?.length;

  if (loading) {
    return (
      <FormControl margin="normal" fullWidth>
        <InputLabel>{label}</InputLabel>
        <CircularProgress size={20} />
      </FormControl>
    );
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={hasError || !!error}
      fullWidth
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(String(e.target.value))}
      >
        <MenuItem value="">(escolha um órgão)</MenuItem>
        {items.map(o => (
          <MenuItem key={o.slug} value={o.slug}>
            {o.name} {o.acronym ? `(${o.acronym})` : ''}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {error
          ? `Falha: ${error}`
          : schema.description ?? 'Órgão carregado do CMS X-Via'}
      </FormHelperText>
    </FormControl>
  );
};

export const XviaOrganizationPickerExtension = FormFieldBlueprint.make({
  name: 'xvia-organization-picker',
  params: {
    field: async () => createFormField({
      name: 'XviaOrganizationPicker',
      component: Component,
    }),
  },
});
